/**
 * Role badge component for DEX UI.
 * Displays the current user's role with color-coded indicator.
 */

import { getState, subscribe } from '../../state/store.js';
import { maskApiKey } from '../../api/auth.js';

/**
 * Get the display name for a role.
 * @param {string} role - The role identifier
 * @returns {string} Human-readable role name
 */
function getRoleDisplayName(role) {
  const names = {
    'super_admin': 'Super Admin',
    'admin': 'Admin',
    'writer': 'Writer',
    'reader': 'Reader'
  };
  return names[role] || role;
}

/**
 * Render the role badge into a container.
 * @param {HTMLElement} container - Container element
 */
export function renderRoleBadge(container) {
  const { session } = getState();

  if (!session.authenticated) {
    container.innerHTML = '';
    return;
  }

  const maskedKey = maskApiKey(session.apiKey);

  const partyId = session.partyId || '';

  container.innerHTML = `
    <div class="flex items-center gap-2">
      <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100">
        <span class="w-2 h-2 rounded-full role-dot-${session.role}"></span>
        <span class="text-sm font-medium role-text-${session.role}">
          ${getRoleDisplayName(session.role)}
        </span>
      </span>
      ${partyId ? `
        <span class="text-xs text-gray-600 font-mono" title="Party ID">
          ${partyId}
        </span>
      ` : ''}
      <span class="text-xs text-gray-400 hidden sm:inline" title="${session.apiKey}">
        ${maskedKey}
      </span>
    </div>
  `;
}

/**
 * Initialize role badge with state subscription.
 * @param {HTMLElement} container - Container element
 * @returns {Function} Cleanup function
 */
export function initRoleBadge(container) {
  // Initial render
  renderRoleBadge(container);

  // Subscribe to state changes
  const unsubscribe = subscribe((state, path) => {
    if (path.includes('session') || path === '*') {
      renderRoleBadge(container);
    }
  });

  return unsubscribe;
}
